import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { RefreshCw, LogOut } from "lucide-react";


//For user backend data storage

const TOKEN_KEY = "ct_login_token";


// ── Refresh interval (ms) ──────────────────────────────────────
const REFRESH_INTERVAL = 5000;

// ── Exchange segment → human-readable label ───────────────────
// Dhan exchangeSegment values from API docs:
// NSE_EQ, BSE_EQ → equity; NSE_FNO, BSE_FNO → F&O; IDX_I → Index; MCX_COMM → MCX
const EXCHANGE_LABEL = {
  NSE_EQ:   "NSE",
  BSE_EQ:   "BSE",
  NSE_FNO:  "NSE F&O",
  BSE_FNO:  "BSE F&O",
  IDX_I:    "INDEX",
  MCX_COMM: "MCX",
  NSE_CURR: "NSE CURR",
  BSE_CURR: "BSE CURR",
};

// ── Product type display ───────────────────────────────────────
const PRODUCT_LABEL = {
  CNC:      "CNC",
  INTRADAY: "INTRA",
  MARGIN:   "MARGIN",
  MTF:      "MTF",
  CO:       "CO",
  BO:       "BO",
};

// ─────────────────────────────────────────────────────────────────
// NORMALIZE: raw Dhan position → our schema
// positionType: LONG / SHORT
// netQty === 0  → closed position
// avgPrice: buyAvg for LONG, sellAvg for SHORT
// LTP and live P&L are enriched after fetch via marketfeed/ltp
// ─────────────────────────────────────────────────────────────────
function normalizeDhanPosition(p, acc) {
  const isLong   = (p.positionType || "").toUpperCase() === "LONG";
  const netQty   = p.netQty   ?? 0;
  const buyAvg   = p.buyAvg   ?? 0;
  const sellAvg  = p.sellAvg  ?? 0;
  const avgPrice = isLong ? buyAvg : sellAvg;
  const isOpen   = netQty !== 0;

  return {
    // identification
    securityId:      p.securityId    || "",
    exchangeSegment: p.exchangeSegment || "",
    // display
    userId:          acc.id,
    username:        acc.name         || acc.id,
    accessToken:     acc.accessToken  || "",
    proxyHost:       acc.proxyHost    || "",
    proxyPort:       acc.proxyPort    || "443",
    proxyUser:       acc.proxyUser    || "",
    proxyPass:       acc.proxyPass    || "",
    symbol:          p.tradingSymbol  || p.securityId || "—",
    tradingSymbol:   p.tradingSymbol  || "",
    exchange:        EXCHANGE_LABEL[p.exchangeSegment] || p.exchangeSegment || "—",
    product:         PRODUCT_LABEL[p.productType] || p.productType || "—",
    productType:     p.productType    || "INTRADAY",
    positionType:    p.positionType   || "—",    // LONG / SHORT
    isOpen,
    // quantities
    netQty,
    buyQty:          p.buyQty         ?? 0,
    sellQty:         p.sellQty        ?? 0,
    // prices
    avgPrice,
    buyAvg,
    sellAvg,
    costPrice:       p.costPrice      ?? 0,
    // derivative fields
    drvOptionType:   p.drvOptionType  || null,   // CE / PE / null
    drvStrikePrice:  p.drvStrikePrice ?? 0,
    drvExpiryDate:   p.drvExpiryDate  || null,
    // P&L from API (realtime since Dhan API update)
    realizedProfit:   p.realizedProfit   ?? 0,
    unrealizedProfit: p.unrealizedProfit ?? 0,
    totalPnl:        (p.realizedProfit ?? 0) + (p.unrealizedProfit ?? 0),
    // LTP — enriched separately via marketfeed/ltp
    ltp: null,
  };
}

// ─────────────────────────────────────────────────────────────────
// SERVER CALLS
// ─────────────────────────────────────────────────────────────────
async function fetchDhanPositions(accessToken, clientId) {
  try {
    const res = await fetch("/api/positions/dhan", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ accessToken, clientId }),
    });
    const data = await res.json();
    return data.positions || [];
  } catch { return []; }
}

async function fetchLTPBatch(accessToken, clientId, instruments) {
  // instruments: [{ exchangeSegment, securityId }]
  if (!instruments.length) return {};
  try {
    const res = await fetch("/api/positions/ltp", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ accessToken, clientId, instruments }),
    });
    const data = await res.json();
    return data.ltp || {};  // { "NSE_FNO:52175": 123.45, ... }
  } catch { return {}; }
}

async function exitAllPositions(accessToken, clientId) {
  try {
    const res = await fetch("/api/positions/exit-all", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ accessToken, clientId }),
    });
    return await res.json();
  } catch (e) { return { error: e.message }; }
}

async function placeDhanOrder(payload) {
  try {
    const res = await fetch("/api/orders/dhan/place", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) throw new Error(data.error || "Order failed");
    return data;
  } catch (e) {
    return { error: e.message };
  }
}

// ─────────────────────────────────────────────────────────────────
// SORT OPTIONS
// ─────────────────────────────────────────────────────────────────
const SORT_OPTIONS = [
  { value: "symbol",   label: "Symbol"   },
  { value: "username", label: "Account"  },
  { value: "exchange", label: "Exchange" },
  { value: "netQty",   label: "Qty"      },
  { value: "totalPnl", label: "P&L"      },
  { value: "ltp",      label: "LTP"      },
];

const CLIENT_ID_COLORS = [
  "#ff2d55", // velvet red
  "#00d97e", // velvet green
  "#ffd60a", // velvet yellow
  "#bf5af2", // velvet purple
  "#00c7ff", // velvet blue
  "#ff8c00", // velvet orange
  "#ff4fd8", // velvet pink
  "#64d2ff", // velvet cyan
];

function getClientIdColor(clientId) {
  const value = String(clientId || "");
  let hash = 0;

  for (let i = 0; i < value.length; i += 1) {
    hash = value.charCodeAt(i) + ((hash << 5) - hash);
  }

  return CLIENT_ID_COLORS[Math.abs(hash) % CLIENT_ID_COLORS.length];
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function Positions() {
  const [accounts,         setAccounts]         = useState([]);
  const [selectedAccount,  setSelectedAccount]  = useState("ALL");
  const [positions,        setPositions]        = useState([]);
  const [loading,          setLoading]          = useState(false);
  const [lastRefreshed,    setLastRefreshed]    = useState(null);
  const [search,           setSearch]           = useState("");
  const [sortBy,           setSortBy]           = useState("symbol");
  const [sortDir,          setSortDir]          = useState("asc");
  const [showClosed,       setShowClosed]       = useState(false);
  const [autoRefresh,      setAutoRefresh]      = useState(true);
  const [exitAllLoading,   setExitAllLoading]   = useState(false);
  const [exitAllMsg,       setExitAllMsg]       = useState("");
  const [confirmExitAll,   setConfirmExitAll]   = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [positionAction,   setPositionAction]   = useState({
    mode: "MARKET",
    quantity: "",
    price: "",
    triggerPrice: "",
  });
  const [positionActionLoading, setPositionActionLoading] = useState(false);
  const [positionActionMsg, setPositionActionMsg] = useState("");

  const intervalRef      = useRef(null);
  const accountsRef      = useRef([]);
  const confirmExitRef   = useRef(false); // ← ref so async handler always sees current value

  // ── Load accounts from login-user backend data ───────────────
useEffect(() => {
  const token = localStorage.getItem(TOKEN_KEY);

  if (!token) {
    setAccounts([]);
    accountsRef.current = [];
    return;
  }

  fetch("/api/login/data/accounts", {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then(res => res.json())
    .then(data => {
      const loaded = data.accounts || [];
      setAccounts(loaded);
      accountsRef.current = loaded;
      localStorage.setItem("ct_accounts", JSON.stringify(loaded));
    })
    .catch(() => {
      setAccounts([]);
      accountsRef.current = [];
    });
}, []);


  useEffect(() => { accountsRef.current = accounts; }, [accounts]);

  // ─────────────────────────────────────────────────────────────
  // FETCH SNAPSHOT: positions + LTP enrichment
  // ─────────────────────────────────────────────────────────────
  const fetchSnapshot = useCallback(async () => {
    setLoading(true);
    try {
      const isDhan = (a) => (a.broker || "").toLowerCase() === "dhan";
      const accsToFetch = selectedAccount === "ALL"
        ? accountsRef.current.filter(a => isDhan(a) && a.accessToken)
        : accountsRef.current.filter(a => a.id === selectedAccount && isDhan(a) && a.accessToken);

      const allPositions = [];

      for (const acc of accsToFetch) {
        const raw = await fetchDhanPositions(acc.accessToken, acc.id);
        const normalized = raw.map(p => normalizeDhanPosition(p, acc));

        // Enrich with LTP via marketfeed/ltp
        const instruments = normalized
          .filter(p => p.securityId && p.exchangeSegment)
          .map(p => ({ exchangeSegment: p.exchangeSegment, securityId: p.securityId }));

        let ltpMap = {};
        if (instruments.length > 0) {
          ltpMap = await fetchLTPBatch(acc.accessToken, acc.id, instruments);
        }

        normalized.forEach(p => {
          const key = `${p.exchangeSegment}:${p.securityId}`;
          if (ltpMap[key] != null) {
            p.ltp = ltpMap[key];
            // Recalculate live unrealized P&L from LTP if API gives 0
            // For LONG:  (LTP - avgPrice) * |netQty|
            // For SHORT: (avgPrice - LTP)  * |netQty|
            if (p.unrealizedProfit === 0 && p.ltp && p.avgPrice && p.netQty !== 0) {
              const direction = p.positionType === "LONG" ? 1 : -1;
              p.unrealizedProfit = direction * (p.ltp - p.avgPrice) * Math.abs(p.netQty);
              p.totalPnl = p.realizedProfit + p.unrealizedProfit;
            }
          }
        });

        allPositions.push(...normalized);
      }

      setPositions(allPositions);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error("Positions fetch error:", err);
    }
    setLoading(false);
  }, [selectedAccount]);

  // ── Initial fetch + on account change ────────────────────────
  useEffect(() => {
    if (accounts.length === 0) return;
    setPositions([]);
    fetchSnapshot();
  }, [accounts, selectedAccount]);

  // ── Auto-refresh ──────────────────────────────────────────────
  useEffect(() => {
    clearInterval(intervalRef.current);
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchSnapshot, REFRESH_INTERVAL);
    }
    return () => clearInterval(intervalRef.current);
  }, [fetchSnapshot, autoRefresh]);

  // ─────────────────────────────────────────────────────────────
  // EXIT ALL HANDLER
  // ─────────────────────────────────────────────────────────────
  const handleExitAll = async () => {
    // First click → show confirm state
    if (!confirmExitRef.current) {
      confirmExitRef.current = true;
      setConfirmExitAll(true);
      return;
    }

    // Second click → execute
    confirmExitRef.current = false;
    setConfirmExitAll(false);
    setExitAllLoading(true);
    setExitAllMsg("");

    const isDhan = (a) => (a.broker || "").toLowerCase() === "dhan";
    const accsToExit = selectedAccount === "ALL"
      ? accountsRef.current.filter(a => isDhan(a) && a.accessToken)
      : accountsRef.current.filter(a => a.id === selectedAccount && isDhan(a) && a.accessToken);

    // Guard: no valid Dhan accounts found
    if (accsToExit.length === 0) {
      setExitAllMsg("⚠ No Dhan accounts found to exit");
      setExitAllLoading(false);
      setTimeout(() => setExitAllMsg(""), 4000);
      return;
    }

    const results = [];
    for (const acc of accsToExit) {
      console.log("Calling exit-all for:", acc.id, acc.name);
      const result = await exitAllPositions(acc.accessToken, acc.id);
      console.log("Exit-all result for", acc.id, ":", result);
      results.push({ acc: acc.name || acc.id, result });
    }

    const failed  = results.filter(r => r.result?.error);
    const success = results.filter(r => !r.result?.error);

    if (failed.length === 0) {
      setExitAllMsg(`✅ Exit All sent for ${success.length} account(s)`);
    } else if (success.length > 0) {
      setExitAllMsg(`⚠ ${success.length}/${results.length} succeeded. Failed: ${failed.map(f => f.acc).join(", ")}`);
    } else {
      setExitAllMsg(`❌ Failed: ${failed.map(f => f.result?.error || "unknown error").join(" | ")}`);
    }

    setExitAllLoading(false);
    setTimeout(() => setExitAllMsg(""), 6000);
    setTimeout(fetchSnapshot, 1500);
  };

  // ─────────────────────────────────────────────────────────────
  // DERIVED STATS
  // ─────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const open   = positions.filter(p => p.isOpen);
    const closed  = positions.filter(p => !p.isOpen);
    const totalPnl = positions.reduce((s, p) => s + (p.totalPnl || 0), 0);
    const openPnl  = open.reduce((s, p)   => s + (p.unrealizedProfit || 0), 0);
    const closedPnl = closed.reduce((s, p) => s + (p.realizedProfit   || 0), 0);
    return { total: positions.length, open: open.length, closed: closed.length, totalPnl, openPnl, closedPnl };
  }, [positions]);

  // ─────────────────────────────────────────────────────────────
  // FILTERED + SORTED TABLE ROWS
  // ─────────────────────────────────────────────────────────────
  const filteredPositions = useMemo(() => {
    let data = [...positions];

    // Filter by account
    if (selectedAccount !== "ALL") {
      data = data.filter(p => p.userId === selectedAccount);
    }

    // Filter open/closed
    if (!showClosed) {
      data = data.filter(p => p.isOpen);
    }

    // Search
    if (search.trim()) {
      const s = search.toLowerCase();
      data = data.filter(p =>
        p.symbol?.toLowerCase().includes(s)   ||
        p.userId?.toLowerCase().includes(s)   ||
        p.username?.toLowerCase().includes(s) ||
        p.exchange?.toLowerCase().includes(s) ||
        p.product?.toLowerCase().includes(s)
      );
    }

    // Sort
    data.sort((a, b) => {
      const va = a[sortBy] ?? "";
      const vb = b[sortBy] ?? "";
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      return 0;
    });

    return data;
  }, [positions, selectedAccount, showClosed, search, sortBy, sortDir]);

  // ─────────────────────────────────────────────────────────────
  // STYLES (matching Orders.js dark theme)
  // ─────────────────────────────────────────────────────────────
  const inp = {
    background: "#1e293b", border: "1px solid #334155", color: "#e2e8f0",
    borderRadius: 6, padding: "8px 12px", fontSize: 13, outline: "none",
  };

  const pnlColor = (v) => v > 0 ? "#22c55e" : v < 0 ? "#ef4444" : "#94a3b8";
  const pnlSign  = (v) => v > 0 ? `+₹${v.toFixed(2)}` : v < 0 ? `-₹${Math.abs(v).toFixed(2)}` : "₹0.00";

  const openPositionView = (position) => {
    setSelectedPosition(position);
    setPositionAction({
      mode: "MARKET",
      quantity: String(Math.abs(position.netQty || 0)),
      price: position.ltp != null ? String(position.ltp) : "",
      triggerPrice: "",
    });
    setPositionActionMsg("");
  };

  const closePositionView = () => {
    setSelectedPosition(null);
    setPositionActionMsg("");
  };

  const exitSideForPosition = (position) =>
    position?.positionType === "LONG" ? "SELL" : "BUY";

  const buildPositionOrderPayload = () => {
    const mode = positionAction.mode;
    const side = exitSideForPosition(selectedPosition);
    const isMarket = mode === "MARKET";
    const isStopLoss = mode === "STOP_LOSS";

    return {
      accessToken: selectedPosition?.accessToken || "",
      clientId: selectedPosition?.userId || "",
      transactionType: side,
      exchangeSegment: selectedPosition?.exchangeSegment || "",
      productType: selectedPosition?.productType || "INTRADAY",
      orderType: isStopLoss ? "STOP_LOSS" : isMarket ? "MARKET" : "LIMIT",
      validity: "DAY",
      securityId: selectedPosition?.securityId || "",
      quantity: Number(positionAction.quantity || Math.abs(selectedPosition?.netQty || 0)),
      tradingSymbol: selectedPosition?.tradingSymbol || selectedPosition?.symbol || "",
      price: Number(isMarket ? 0 : positionAction.price || 0),
      triggerPrice: Number(isStopLoss ? positionAction.triggerPrice || 0 : 0),
      disclosedQuantity: 0,
      proxyHost: selectedPosition?.proxyHost || "",
      proxyPort: selectedPosition?.proxyPort || "443",
      proxyUser: selectedPosition?.proxyUser || "",
      proxyPass: selectedPosition?.proxyPass || "",
    };
  };

  const submitPositionAction = async () => {
    if (!selectedPosition?.isOpen || positionActionLoading) return;

    const payload = buildPositionOrderPayload();
    if (!payload.quantity || payload.quantity <= 0) {
      setPositionActionMsg("Quantity is required");
      return;
    }
    if (payload.orderType === "LIMIT" && !payload.price) {
      setPositionActionMsg("Limit price is required");
      return;
    }
    if (payload.orderType === "STOP_LOSS" && (!payload.price || !payload.triggerPrice)) {
      setPositionActionMsg("SL limit price and trigger price are required");
      return;
    }

    const label = `${payload.transactionType} ${payload.orderType} ${payload.quantity} ${selectedPosition.symbol}`;
    if (!window.confirm(`Send ${label}?`)) return;

    setPositionActionLoading(true);
    setPositionActionMsg("");
    const result = await placeDhanOrder(payload);
    setPositionActionLoading(false);

    if (result.error) {
      setPositionActionMsg(result.error);
      return;
    }

    closePositionView();
    setTimeout(fetchSnapshot, 1200);
  };

  const statCards = [
    {
      label: "Total Positions",
      value: `${stats.open} open · ${stats.closed} closed`,
      icon: "📊", iconBg: "#3b82f6",
      sub: `${stats.total} total`,
    },
    {
      label: "Open P&L",
      value: pnlSign(stats.openPnl),
      icon: "📈", iconBg: stats.openPnl >= 0 ? "#22c55e" : "#ef4444",
      color: pnlColor(stats.openPnl),
    },
    {
      label: "Realized P&L",
      value: pnlSign(stats.closedPnl),
      icon: "✅", iconBg: stats.closedPnl >= 0 ? "#22c55e" : "#ef4444",
      color: pnlColor(stats.closedPnl),
    },
    {
      label: "Total P&L (Day)",
      value: pnlSign(stats.totalPnl),
      icon: "💰", iconBg: stats.totalPnl >= 0 ? "#22c55e" : "#ef4444",
      color: pnlColor(stats.totalPnl),
      bold: true,
    },
    {
      label: "Active Positions",
      value: stats.open,
      icon: "🔥", iconBg: "#f59e0b", color: "#f59e0b",
    },
  ];

  // ─────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="mobile-page mobile-positions" style={{ padding: 24, color: "#e2e8f0", minHeight: "100vh", background: "#0f172a" }}>
      {/* ── HEADER ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 22 }}>📊</span>
          <h2 style={{ fontSize: 24, fontWeight: "bold", margin: 0 }}>Positions</h2>
          {loading && <span style={{ fontSize: 11, color: "#f59e0b", letterSpacing: 2 }}>⟳ LOADING...</span>}
          {lastRefreshed && !loading && (
            <span style={{ fontSize: 11, color: "#475569" }}>Updated {lastRefreshed.toLocaleTimeString()}</span>
          )}
          {autoRefresh && (
            <span style={{ fontSize: 11, color: "#22c55e", letterSpacing: 1, fontWeight: "bold" }}>● LIVE 5s</span>
          )}
        </div>

        {/* Right-side action buttons */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {exitAllMsg && (
            <span style={{ fontSize: 12, color: exitAllMsg.startsWith("✅") ? "#22c55e" : "#f59e0b", fontWeight: "bold" }}>
              {exitAllMsg}
            </span>
          )}

          <button
            onClick={fetchSnapshot}
            className="btn-solid-green"
            style={{
              padding: "9px 18px",
              borderRadius: 8,
              cursor: "pointer",
              fontSize: 13,
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <RefreshCw size={15} /> Refresh
          </button>

          {/* EXIT ALL — confirm on first click, execute on second */}
          <button
            onClick={handleExitAll}
            disabled={exitAllLoading || stats.open === 0}
            className="btn-maroon-red"
            style={{
              padding: "9px 18px",
              borderRadius: 8,
              cursor: stats.open === 0 ? "not-allowed" : "pointer",
              fontSize: 13,
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: 6,
              opacity: stats.open === 0 ? 0.5 : 1,
              transition: "all 0.2s",
            }}
          >
            <LogOut size={15} />
            {exitAllLoading ? "Exiting..." : confirmExitAll ? "⚠ Confirm Exit All?" : "Exit All"}
          </button>
          {confirmExitAll && (
            <button
              style={{ background: "transparent", border: "1px solid #334155", color: "#94a3b8", padding: "9px 14px", borderRadius: 8, cursor: "pointer", fontSize: 13 }}
            onClick={() => { confirmExitRef.current = false; setConfirmExitAll(false); }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* ── ACCOUNT + OPTIONS ── */}
      <div style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 10, padding: "14px 18px", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <label style={{ fontSize: 13, color: "#94a3b8", whiteSpace: "nowrap" }}>Select Account:</label>
          <select
            value={selectedAccount}
            onChange={e => { setSelectedAccount(e.target.value); setConfirmExitAll(false); }}
            style={{ ...inp, flex: 1, minWidth: 220, maxWidth: 400 }}
          >
            <option value="ALL">All Accounts</option>
            {accounts
              .filter(a => (a.broker || "").toLowerCase() === "dhan")
              .map(acc => (
                <option key={acc.id} value={acc.id}>
                  {acc.name || acc.id} ({acc.id}) — {acc.broker}
                </option>
              ))}
          </select>

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", userSelect: "none" }}>
            <input
              type="checkbox" checked={autoRefresh}
              onChange={() => setAutoRefresh(v => !v)}
              style={{ width: 15, height: 15, accentColor: "#3b82f6" }}
            />
            <span style={{ color: "#94a3b8" }}>Auto-refresh (5s)</span>
          </label>

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", userSelect: "none" }}>
            <input
              type="checkbox" checked={showClosed}
              onChange={() => setShowClosed(v => !v)}
              style={{ width: 15, height: 15, accentColor: "#94a3b8" }}
            />
            <span style={{ color: "#94a3b8" }}>Show closed positions</span>
          </label>
        </div>
      </div>

      {/* ── SEARCH + SORT ── */}
      <div style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 10, padding: "12px 18px", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220, position: "relative" }}>
            <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#475569", fontSize: 14 }}>🔍</span>
            <input
              placeholder="Search symbol, account, exchange, product..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ ...inp, width: "100%", paddingLeft: 32, boxSizing: "border-box" }}
            />
          </div>
          <label style={{ fontSize: 13, color: "#94a3b8", whiteSpace: "nowrap" }}>Sort by:</label>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...inp, minWidth: 120 }}>
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button
            onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")}
            style={{ ...inp, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontWeight: "bold", minWidth: 72, justifyContent: "center" }}
          >
            {sortDir === "asc" ? "↑" : "↓"} {sortDir.toUpperCase()}
          </button>
        </div>
      </div>

      {/* ── STAT CARDS ── */}
      <div className="mobile-stat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 14, marginBottom: 20 }}>
        {statCards.map(card => (
          <div key={card.label} style={{
            background: "#1e293b", border: "1px solid #334155", borderRadius: 10,
            padding: "16px 18px", display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <div>
              <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 6 }}>{card.label}</div>
              <div style={{
                fontSize: card.sub ? 18 : 24,
                fontWeight: "bold",
                color: card.color || "#e2e8f0",
              }}>{card.value}</div>
              {card.sub && <div style={{ fontSize: 11, color: "#475569", marginTop: 2 }}>{card.sub}</div>}
            </div>
            <div style={{
              width: 48, height: 48, borderRadius: "50%", background: card.iconBg,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 20, boxShadow: `0 0 14px ${card.iconBg}80`,
            }}>{card.icon}</div>
          </div>
        ))}
      </div>

      {/* ── POSITIONS TABLE ── */}
      <div style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderBottom: "1px solid #334155" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{ fontSize: 16, fontWeight: "bold" }}>Active Positions</span>
            <span style={{
              background: "#0f172a", border: "1px solid #334155",
              borderRadius: 20, padding: "2px 12px", fontSize: 12, color: "#94a3b8",
            }}>
              {stats.open} open · {stats.closed} closed · {stats.total} total
            </span>
          </div>
          <span style={{ fontSize: 12, color: "#475569" }}>{filteredPositions.length} shown</span>
        </div>

        <div className="mobile-scroll-table" style={{ overflowX: "auto" }}>
          <table className="mobile-data-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#0f172a" }}>
                {[
                  { label: "ACTIONS"      },
                  { label: "ACCOUNT",     key: "username"  },
                  { label: "SYMBOL",      key: "symbol"    },
                  { label: "EXCHANGE"                      },
                  { label: "PRODUCT"                       },
                  { label: "DIRECTION"                     },
                  { label: "QTY",         key: "netQty"    },
                  { label: "AVG PRICE",   key: "avgPrice"  },
                  { label: "LTP",         key: "ltp"       },
                  { label: "OPEN P&L",    key: "unrealizedProfit" },
                  { label: "REALIZED P&L",key: "realizedProfit"   },
                  { label: "TOTAL P&L",   key: "totalPnl"  },
                  { label: "STATUS"                        },
                ].map(h => (
                  <th
                    key={h.label}
                    onClick={() => h.key && (setSortBy(h.key), setSortDir(d => d === "asc" ? "desc" : "asc"))}
                    style={{
                      padding: "10px 14px", textAlign: "left", color: "#475569",
                      fontWeight: "600", fontSize: 10, letterSpacing: 1, whiteSpace: "nowrap",
                      cursor: h.key ? "pointer" : "default", borderBottom: "1px solid #334155",
                    }}
                  >
                    {h.label}
                    {h.key && sortBy === h.key && (
                      <span style={{ marginLeft: 4, color: "#3b82f6" }}>{sortDir === "asc" ? "↑" : "↓"}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredPositions.length === 0 ? (
                <tr>
                  <td colSpan="13" style={{ textAlign: "center", padding: "60px 0", color: "#475569" }}>
                    {loading ? "Loading positions..." : accounts.length === 0 ? "No Dhan accounts configured" : "No positions found"}
                  </td>
                </tr>
              ) : (
                filteredPositions.map((p, i) => {
                  const isLong   = p.positionType === "LONG";
                  const dirColor = isLong ? "#22c55e" : "#ef4444";
                  const pnlOpen  = p.unrealizedProfit || 0;
                  const pnlReal  = p.realizedProfit   || 0;
                  const pnlTotal = p.totalPnl         || 0;

                  // Build symbol display: symbol + option details if F&O
                  const symbolDisplay = p.drvOptionType
                    ? `${p.symbol} ${p.drvStrikePrice > 0 ? p.drvStrikePrice : ""} ${p.drvOptionType}`
                    : p.symbol;

                  return (
                    <tr
                      key={`${p.userId}-${p.securityId}-${i}`}
                      style={{
                        borderBottom: "1px solid #1e293b",
                        background: !p.isOpen
                          ? "#0c1524"
                          : i % 2 === 0 ? "#0f172a" : "#1e293b",
                        opacity: p.isOpen ? 1 : 0.6,
                      }}
                    >
                      {/* ACTIONS */}
                      <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                        {p.isOpen ? (
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              title="View position actions"
                              style={{
                                background: "#1e3a5f", border: "1px solid #38bdf840",
                                color: "#38bdf8", padding: "3px 10px", borderRadius: 4,
                                cursor: "pointer", fontSize: 10, fontWeight: "bold",
                              }}
                              onClick={() => openPositionView(p)}
                            >
                              View
                            </button>
                          </div>
                        ) : (
                          <span style={{ fontSize: 10, color: "#475569" }}>—</span>
                        )}
                      </td>

                      {/* ACCOUNT */}
                      <td style={{ padding: "10px 14px" }}>
                        <div
                          style={{
                            display: "inline-block",
                            color: getClientIdColor(p.userId),
                            background: `${getClientIdColor(p.userId)}30`,
                            border: `1px solid ${getClientIdColor(p.userId)}`,
                            boxShadow: `inset 0 0 10px ${getClientIdColor(p.userId)}25`,
                            borderRadius: 6,
                            padding: "4px 8px",
                            fontWeight: "bold",
                          }}
                        >
                          <div>{p.username}</div>
                          <div style={{ fontSize: 10 }}>{p.userId}</div>
                        </div>
                      </td>

                      {/* SYMBOL */}
                      <td style={{ padding: "10px 14px" }}>
                        <div style={{ fontWeight: "bold", color: "#38bdf8", fontSize: 13 }}>{symbolDisplay}</div>
                        {p.drvExpiryDate && p.drvExpiryDate !== "0001-01-01" && (
                          <div style={{ fontSize: 10, color: "#475569" }}>Exp: {p.drvExpiryDate}</div>
                        )}
                      </td>

                      {/* EXCHANGE */}
                      <td style={{ padding: "10px 14px" }}>
                        <span style={{
                          background: "#0f172a", border: "1px solid #1e3a5f",
                          color: "#7dd3fc", padding: "2px 8px", borderRadius: 4,
                          fontSize: 10, fontWeight: "bold", letterSpacing: 1,
                        }}>
                          {p.exchange}
                        </span>
                      </td>

                      {/* PRODUCT */}
                      <td style={{ padding: "10px 14px", color: "#94a3b8", fontSize: 11 }}>{p.product}</td>

                      {/* DIRECTION */}
                      <td style={{ padding: "10px 14px" }}>
                        <span style={{
                          background: dirColor + "20", border: `1px solid ${dirColor}40`,
                          color: dirColor, padding: "2px 10px", borderRadius: 20,
                          fontSize: 10, fontWeight: "bold",
                        }}>
                          {isLong ? "▲ LONG" : "▼ SHORT"}
                        </span>
                      </td>

                      {/* QTY */}
                      <td style={{ padding: "10px 14px", textAlign: "right" }}>
                        <div style={{ color: "#e2e8f0", fontWeight: "bold" }}>{Math.abs(p.netQty)}</div>
                        {(p.buyQty > 0 || p.sellQty > 0) && (
                          <div style={{ fontSize: 10, color: "#475569" }}>
                            B:{p.buyQty} S:{p.sellQty}
                          </div>
                        )}
                      </td>

                      {/* AVG PRICE — buyAvg for LONG, sellAvg for SHORT */}
                      <td style={{ padding: "10px 14px", textAlign: "right" }}>
                        <div style={{ color: "#e2e8f0" }}>₹{Number(p.avgPrice).toFixed(2)}</div>
                        {isLong && p.sellAvg > 0 && (
                          <div style={{ fontSize: 10, color: "#475569" }}>Sell: ₹{p.sellAvg.toFixed(2)}</div>
                        )}
                        {!isLong && p.buyAvg > 0 && (
                          <div style={{ fontSize: 10, color: "#475569" }}>Buy: ₹{p.buyAvg.toFixed(2)}</div>
                        )}
                      </td>

                      {/* LTP */}
                      <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: "bold", color: "#e2e8f0" }}>
                        {p.ltp != null ? `₹${Number(p.ltp).toFixed(2)}` : <span style={{ color: "#475569" }}>—</span>}
                      </td>

                      {/* OPEN P&L (unrealized) */}
                      <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: "bold", color: pnlColor(pnlOpen) }}>
                        {pnlSign(pnlOpen)}
                      </td>

                      {/* REALIZED P&L */}
                      <td style={{ padding: "10px 14px", textAlign: "right", color: pnlColor(pnlReal) }}>
                        {pnlSign(pnlReal)}
                      </td>

                      {/* TOTAL P&L */}
                      <td style={{
                        padding: "10px 14px", textAlign: "right", fontWeight: "bold", fontSize: 13,
                        color: pnlColor(pnlTotal),
                      }}>
                        {pnlSign(pnlTotal)}
                      </td>

                      {/* STATUS */}
                      <td style={{ padding: "10px 14px" }}>
                        <span style={{
                          background: p.isOpen ? "#14532d20" : "#33415520",
                          border: `1px solid ${p.isOpen ? "#22c55e40" : "#33415580"}`,
                          color: p.isOpen ? "#22c55e" : "#94a3b8",
                          padding: "3px 8px", borderRadius: 20, fontSize: 10, fontWeight: "bold",
                        }}>
                          {p.isOpen ? "● OPEN" : "○ CLOSED"}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer note */}
        {accounts.filter(a => a.broker !== "Dhan").length > 0 && (
          <div style={{ padding: "10px 18px", borderTop: "1px solid #1e293b", fontSize: 11, color: "#475569" }}>
            ℹ Positions are currently fetched for Dhan accounts only. Upstox / Zerodha support can be added once their position endpoints are configured.
          </div>
        )}
      </div>
      {selectedPosition && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.72)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 18,
          }}
        >
          <div
            style={{
              width: "min(720px, 100%)",
              maxHeight: "92vh",
              overflowY: "auto",
              background: "#020617",
              border: "1px solid #334155",
              borderRadius: 10,
              boxShadow: "0 24px 70px rgba(0,0,0,.55)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px", borderBottom: "1px solid #334155" }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: "bold" }}>Position Actions</div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>{selectedPosition.symbol}</div>
              </div>
              <button onClick={closePositionView} style={{ background: "transparent", border: "1px solid #334155", color: "#e2e8f0", borderRadius: 6, padding: "6px 10px", cursor: "pointer" }}>Close</button>
            </div>

            <div style={{ padding: 18, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
              {[
                ["Account", selectedPosition.userId],
                ["Exchange", selectedPosition.exchange],
                ["Product", selectedPosition.product],
                ["Direction", selectedPosition.positionType],
                ["Exit Side", exitSideForPosition(selectedPosition)],
                ["Quantity", Math.abs(selectedPosition.netQty)],
                ["Avg Price", `₹${Number(selectedPosition.avgPrice).toFixed(2)}`],
                ["LTP", selectedPosition.ltp != null ? `₹${Number(selectedPosition.ltp).toFixed(2)}` : "—"],
                ["Open P&L", pnlSign(selectedPosition.unrealizedProfit || 0)],
                ["Total P&L", pnlSign(selectedPosition.totalPnl || 0)],
              ].map(([label, value]) => (
                <div key={label} style={{ border: "1px solid #1e293b", borderRadius: 8, padding: "10px 12px", background: "#0f172a" }}>
                  <div style={{ fontSize: 10, color: "#64748b", letterSpacing: 1, marginBottom: 5 }}>{label}</div>
                  <div style={{ fontSize: 13, color: "#e2e8f0", fontWeight: "bold", overflowWrap: "anywhere" }}>{value}</div>
                </div>
              ))}
            </div>

            {selectedPosition.isOpen ? (
              <div style={{ padding: "0 18px 18px" }}>
                <div style={{ borderTop: "1px solid #334155", paddingTop: 16, marginTop: 4 }}>
                  <div style={{ fontSize: 15, fontWeight: "bold", marginBottom: 12 }}>
                    {exitSideForPosition(selectedPosition)} Position
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr 1fr 1fr", gap: 10 }}>
                    <select
                      value={positionAction.mode}
                      onChange={e => setPositionAction(f => ({ ...f, mode: e.target.value }))}
                      style={inp}
                    >
                      <option value="MARKET">Market Exit</option>
                      <option value="LIMIT">Limit Exit</option>
                      <option value="STOP_LOSS">Set SL Limit</option>
                    </select>
                    <input
                      value={positionAction.quantity}
                      onChange={e => setPositionAction(f => ({ ...f, quantity: e.target.value }))}
                      placeholder="Qty"
                      type="number"
                      style={inp}
                    />
                    <input
                      value={positionAction.price}
                      onChange={e => setPositionAction(f => ({ ...f, price: e.target.value }))}
                      placeholder={positionAction.mode === "STOP_LOSS" ? "SL Limit" : "Limit Price"}
                      type="number"
                      disabled={positionAction.mode === "MARKET"}
                      style={{ ...inp, opacity: positionAction.mode === "MARKET" ? .45 : 1 }}
                    />
                    <input
                      value={positionAction.triggerPrice}
                      onChange={e => setPositionAction(f => ({ ...f, triggerPrice: e.target.value }))}
                      placeholder="Trigger"
                      type="number"
                      disabled={positionAction.mode !== "STOP_LOSS"}
                      style={{ ...inp, opacity: positionAction.mode === "STOP_LOSS" ? 1 : .45 }}
                    />
                  </div>

                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                    <button
                      onClick={submitPositionAction}
                      disabled={positionActionLoading}
                      className={positionAction.mode === "STOP_LOSS" ? "btn-maroon-red" : "btn-solid-green"}
                      style={{ borderRadius: 7, padding: "9px 14px", cursor: positionActionLoading ? "not-allowed" : "pointer", fontWeight: "bold" }}
                    >
                      {positionActionLoading ? "Sending..." : positionAction.mode === "STOP_LOSS" ? "Set SL" : `Send ${exitSideForPosition(selectedPosition)}`}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ padding: "0 18px 18px", color: "#64748b", fontSize: 12 }}>
                This position is closed, so actions are disabled.
              </div>
            )}

            {positionActionMsg && (
              <div style={{ margin: "0 18px 18px", padding: "10px 12px", borderRadius: 8, border: "1px solid #334155", color: "#fecaca", background: "#0f172a", fontSize: 12 }}>
                {positionActionMsg}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
